import { Injectable, inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, tap } from 'rxjs';
import { Announcement, AnnouncementCreate, AnnouncementAttachment } from '../models/comunicado.interface';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class ComunicadoService implements OnDestroy {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private apiUrl = '/api/v1/comunicados';

  private announcementsSubject = new BehaviorSubject<Announcement[]>([]);
  public announcements$ = this.announcementsSubject.asObservable();
  
  private pollingIntervalId: any = null;
  private authSubscription: any = null;

  constructor() {
    this.authSubscription = this.authService.isAuthenticated$.subscribe(isAuth => {
      if (isAuth) {
        this.startPolling();
      } else {
        this.stopPolling();
        this.announcementsSubject.next([]);
      }
    });
  }

  public isRead(id: number): boolean {
    const announcement = this.announcementsSubject.value.find(ann => ann.id === id);
    return announcement ? !!announcement.read : false;
  }

  public markAsRead(id: number): void {
    // Update local UI state immediately for instant feedback
    this.updateLocalState(id, true);

    // Call backend endpoint to persist the read status
    this.http.post(`${this.apiUrl}/${id}/read`, {}).subscribe({
      next: () => {
        console.log(`[ComunicadoService] Marked announcement ${id} as read in backend.`);
      },
      error: (err) => {
        console.error(`[ComunicadoService] Failed to mark announcement ${id} as read in backend:`, err);
        // Revert local state if request failed
        this.updateLocalState(id, false);
      }
    });
  }

  public markAsUnread(id: number): void {
    // Update local UI state immediately for instant feedback
    this.updateLocalState(id, false);

    // Call backend endpoint to persist the unread status
    this.http.post(`${this.apiUrl}/${id}/unread`, {}).subscribe({
      next: () => {
        console.log(`[ComunicadoService] Marked announcement ${id} as unread in backend.`);
      },
      error: (err) => {
        console.error(`[ComunicadoService] Failed to mark announcement ${id} as unread in backend:`, err);
        // Revert local state if request failed
        this.updateLocalState(id, true);
      }
    });
  }

  private updateLocalState(id: number, read: boolean): void {
    const current = this.announcementsSubject.value;
    const updated = current.map(ann => {
      if (ann.id === id) {
        return { ...ann, read };
      }
      return ann;
    });
    this.announcementsSubject.next(updated);
  }

  public refreshAnnouncements(): Observable<Announcement[]> {
    return this.getActiveAnnouncements().pipe(
      tap({
        next: (data) => {
          const mapped = data.sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
          this.announcementsSubject.next(mapped);
        },
        error: (err) => {
          console.error('[ComunicadoService] Error during background refresh:', err);
        }
      })
    );
  }

  public startPolling(intervalMs: number = 10000): void {
    if (this.pollingIntervalId) {
      return; // Already polling
    }
    
    // Initial fetch
    this.refreshAnnouncements().subscribe();
    
    // Poll periodically
    this.pollingIntervalId = setInterval(() => {
      this.refreshAnnouncements().subscribe();
    }, intervalMs);
  }

  public stopPolling(): void {
    if (this.pollingIntervalId) {
      clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = null;
    }
  }

  getActiveAnnouncements(): Observable<Announcement[]> {
    return this.http.get<Announcement[]>(`${this.apiUrl}/active`);
  }

  getAllAnnouncements(): Observable<Announcement[]> {
    return this.http.get<Announcement[]>(`${this.apiUrl}/`);
  }

  createAnnouncement(announcement: AnnouncementCreate): Observable<Announcement> {
    return this.http.post<Announcement>(`${this.apiUrl}/`, announcement);
  }

  deleteAnnouncement(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  uploadAttachment(file: File): Observable<AnnouncementAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<AnnouncementAttachment>(`${this.apiUrl}/upload-attachment`, formData);
  }

  resendAnnouncementEmails(id: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${id}/resend`, {});
  }

  ngOnDestroy(): void {
    this.stopPolling();
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }
}
